import { __assign } from "tslib";
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ddt from '@wix/da-ddt';
import { BiDataHook, ReferralType } from '@wix/da-bi/pkg/constants';
import { SubscriptionPurchaseFTUEContext } from '../../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import { useIsCurrentUserDeviationAuthor } from '../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import SubscribeToUnlockTooltip from '../../_partials/SubscribeToUnlockTooltip';
import { useDeviationDownloads } from './permissions';
import DownloadButtonInner from './DownloadButtonInner';
import DownloadPopover from './DownloadPopover';
import s from './DownloadButton.scss';
var logger = ddt.createLogger('downloadbutton');
/** There are two types of download available for deviations: free and premium
 *  and the deviation can have one/both/none - all controled through this button. */
var DownloadButton = function (_a) {
    var deviation = _a.deviation, onDownload = _a.onDownload, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave;
    var t = useTranslation().t;
    var _b = useRestrictToLoggedIn(), isLoggedIn = _b.isLoggedIn, restrictToLoggedIn = _b.restrictToLoggedIn;
    var isOwner = useIsCurrentUserDeviationAuthor(deviation);
    var tierAccess = deviation.tierAccess;
    logger.log('DownloadButton, deviation:', deviation);
    logger.log('isOwner', isOwner, 'tierAccess', tierAccess);
    // when the user first purchases a subscription, open the tooltip and highlight the icon
    var subscriptionPurchaseFTUEPhase = useContext(SubscriptionPurchaseFTUEContext);
    var isButtonHighlighted = subscriptionPurchaseFTUEPhase === 'tooltips';
    // when there's unpurchased premium or both free and premium, show a popover
    var renderDownloadPopover = useCallback(function (_a) {
        var closeTooltip = _a.closeTooltip;
        return (React.createElement(DownloadPopover, { deviation: deviation, closeTooltip: closeTooltip, onDownload: onDownload }));
    }, [deviation, onDownload]);
    var downloadsAndPermissions = useDeviationDownloads(deviation);
    var freeDownload = downloadsAndPermissions.freeDownload, premiumDownload = downloadsAndPermissions.premiumDownload;
    var hasNeitherDownload = !premiumDownload && !freeDownload;
    if (hasNeitherDownload) {
        logger.log('has no downloads');
        return null;
    }
    // send anon to sisu for both free and premium downloads
    if (!isLoggedIn) {
        var tooltip_1 = t('actionbar.download.mustLogIn');
        return (React.createElement(ActionBarItemWrapper, { popoverWrapperClassName: s['button-wrapper'], tooltip: tooltip_1, forceVisibleTooltip: isButtonHighlighted },
            React.createElement(DownloadButtonInner, { text: tooltip_1, deviationId: deviation.deviationId, isPaidDownload: !!premiumDownload, isHighlighted: isButtonHighlighted, showPremiumIcon: !!premiumDownload || tierAccess === 'locked', onClick: restrictToLoggedIn(ReferralType.ANON_DOWNLOAD) })));
    }
    // in-tier, un-subbed free downloads are blocked with a special popup
    var hasFreeOnly = !premiumDownload && !!freeDownload;
    var isBlockedBySubscription = tierAccess === 'locked' || tierAccess === 'locked-subscribed';
    if (hasFreeOnly && isBlockedBySubscription) {
        logger.log('free download blocked by tier', tierAccess);
        return (React.createElement(SubscribeToUnlockTooltip, { className: s['button-wrapper'], deviation: deviation, forceVisible: isButtonHighlighted },
            React.createElement(DownloadButtonInner, { disabled: true, text: t('actionbar.download'), deviationId: deviation.deviationId, isHighlighted: isButtonHighlighted })));
    }
    // here, we know the user is logged in and the special case of
    // free downloads in subscription content is handled
    // see the README for a list of rules
    var tooltipKey = getTooltipKey(downloadsAndPermissions);
    var opensPopover = hasDownloadPopover(downloadsAndPermissions);
    var downloadProps = getDownloadProps(downloadsAndPermissions);
    var tooltip = t(tooltipKey);
    logger.log(tooltip, 'opensPopover?', opensPopover, downloadProps);
    return (React.createElement(ActionBarItemWrapper, { popoverWrapperClassName: s['button-wrapper'], tooltip: tooltip, forceVisibleTooltip: isButtonHighlighted, renderPopover: opensPopover ? renderDownloadPopover : undefined, allowMobilePopover: opensPopover },
        React.createElement(DownloadButtonInner, __assign({}, downloadProps, { deviationId: deviation.deviationId, isHighlighted: isButtonHighlighted, text: tooltip, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onClick: downloadProps.downloadUrl ? onDownload : undefined }))));
};
DownloadButton.displayName = 'DownloadButton';
export default DownloadButton;
// ---- the following are just to isolate some of the convoluted access logic,
// see the README for a list of rules
function getTooltipKey(_a) {
    var freeDownloadPermission = _a.freeDownloadPermission, premiumDownloadPermission = _a.premiumDownloadPermission;
    // has both but free is blocked -> downloadable pcp button
    if (freeDownloadPermission === 'blocked_by_subscription' &&
        premiumDownloadPermission === 'downloadable') {
        return 'actionbar.download';
    }
    // has both
    if (freeDownloadPermission && premiumDownloadPermission) {
        return freeDownloadPermission === 'needs_subscription'
            ? 'actionbar.pcp.buyOrSubscribe'
            : 'actionbar.pcp.premiumAndFree';
    }
    if (premiumDownloadPermission === 'purchasable') {
        return 'actionbar.pcp';
    }
    // SubscribeToUnlockTooltip already handles 'needs_subscription'
    // and 'blocked_by_subscription' when there's only a free download
    return 'actionbar.download';
}
function hasDownloadPopover(_a) {
    var freeDownloadPermission = _a.freeDownloadPermission, premiumDownloadPermission = _a.premiumDownloadPermission;
    var hasBoth = Boolean(freeDownloadPermission && premiumDownloadPermission);
    return (premiumDownloadPermission === 'purchasable' ||
        (hasBoth && freeDownloadPermission !== 'blocked_by_subscription'));
}
// just to isolate some of the convoluted access logic,
// see the README for a list of rules
function getDownloadProps(downloadsAndPermissions) {
    var downloadUrl = getDownloadUrl(downloadsAndPermissions);
    var freeDownloadPermission = downloadsAndPermissions.freeDownloadPermission, premiumDownload = downloadsAndPermissions.premiumDownload, premiumDownloadPermission = downloadsAndPermissions.premiumDownloadPermission;
    return premiumDownload
        ? {
            downloadUrl: downloadUrl,
            isPaidDownload: true,
            showPremiumIcon: freeDownloadPermission === 'needs_subscription' ||
                premiumDownloadPermission === 'purchasable',
        }
        : {
            downloadUrl: downloadUrl,
            dataHook: BiDataHook.DEVIATION_DOWNLOAD_BUTTON,
        };
}
function getDownloadUrl(_a) {
    var freeDownload = _a.freeDownload, freeDownloadPermission = _a.freeDownloadPermission, premiumDownload = _a.premiumDownload, premiumDownloadPermission = _a.premiumDownloadPermission;
    var isUsingPopoverInstead = hasDownloadPopover({
        freeDownloadPermission: freeDownloadPermission,
        premiumDownloadPermission: premiumDownloadPermission,
    });
    if (isUsingPopoverInstead) {
        return undefined;
    }
    if (premiumDownloadPermission === 'downloadable') {
        return premiumDownload === null || premiumDownload === void 0 ? void 0 : premiumDownload.url;
    }
    return freeDownload === null || freeDownload === void 0 ? void 0 : freeDownload.url;
}
//# sourceMappingURL=DownloadButton.js.map